import { Link } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { COOKIE_PREFERENCES_EVENT, cookieConsentFromProfile } from '@/shared/lib/cookieConsent'
import { useMeProfile } from '@/features/profile/api/queries'
import { useAnalyticsConfig } from '@/shared/api/analyticsConfigQueries'
import { type ConsentChoice, usePersistCookieConsent } from '@/shared/hooks/useCookieConsent'

const BASE_INVENTORY_ROWS = ['session', 'xsrf', 'consent', 'sentry'] as const

export function CookieBanner() {
  const { t } = useTranslation()
  const profile = useMeProfile()
  const analyticsConfig = useAnalyticsConfig()
  const persistConsent = usePersistCookieConsent()
  const stored = cookieConsentFromProfile(profile.data)
  const [preferencesOpen, setPreferencesOpen] = useState(false)
  const [savedThisSession, setSavedThisSession] = useState(false)
  const [draft, setDraft] = useState<ConsentChoice | null>(null)
  const [showInventory, setShowInventory] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  const open = preferencesOpen || (!profile.isPending && stored === null && !savedThisSession)
  const analytics = draft?.analytics ?? stored?.analytics ?? false
  const functional = draft?.functional ?? stored?.functional ?? false
  const marketing = draft?.marketing ?? stored?.marketing ?? false
  const activeProviders = analyticsConfig.data ?? []
  const hasMarketingProvider = activeProviders.some((provider) => provider.category === 'marketing')
  const inventoryRows = [...BASE_INVENTORY_ROWS, ...activeProviders.map((provider) => provider.key)]

  useEffect(() => {
    const onOpen = (): void => {
      const current = cookieConsentFromProfile(profile.data)
      setDraft({
        analytics: current?.analytics ?? false,
        functional: current?.functional ?? false,
        marketing: current?.marketing ?? false,
      })
      setPreferencesOpen(true)
    }
    window.addEventListener(COOKIE_PREFERENCES_EVENT, onOpen)
    return () => window.removeEventListener(COOKIE_PREFERENCES_EVENT, onOpen)
  }, [profile.data])

  if (!open) {
    return null
  }

  const persist = (next: ConsentChoice): void => {
    setIsSaving(true)
    void persistConsent(next)
      .then(() => {
        setDraft(null)
        setSavedThisSession(true)
        setPreferencesOpen(false)
      })
      .finally(() => {
        setIsSaving(false)
      })
  }

  return (
    <Dialog open={open}>
      <DialogContent
        showCloseButton={false}
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        className="max-h-[90vh] min-w-0 gap-5 overflow-x-hidden overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle>{t('legal.cookies.banner_title')}</DialogTitle>
          <DialogDescription>{t('legal.cookies.banner_intro')}</DialogDescription>
        </DialogHeader>

        <ul className="list-none space-y-3 p-0">
          <li className="space-y-1">
            <label className="inline-flex min-h-6 cursor-pointer items-center gap-2 text-sm">
              <input type="checkbox" checked disabled className="size-5 rounded border-input accent-[var(--v2-brand)]" />
              {t('legal.cookies.essential')}
            </label>
            <p className="pl-7 text-xs text-muted-foreground">{t('legal.cookies.essential_help')}</p>
          </li>
          <li className="space-y-1">
            <label className="inline-flex min-h-6 cursor-pointer items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={analytics}
                onChange={(e) => setDraft({ analytics: e.target.checked, functional, marketing })}
                className="size-5 rounded border-input accent-[var(--v2-brand)]"
              />
              {t('legal.cookies.analytics')}
            </label>
            <p className="pl-7 text-xs text-muted-foreground">{t('legal.cookies.analytics_help')}</p>
          </li>
          {hasMarketingProvider ? (
            <li className="space-y-1">
              <label className="inline-flex min-h-6 cursor-pointer items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={marketing}
                  onChange={(e) => setDraft({ analytics, functional, marketing: e.target.checked })}
                  className="size-5 rounded border-input accent-[var(--v2-brand)]"
                />
                {t('legal.cookies.marketing')}
              </label>
              <p className="pl-7 text-xs text-muted-foreground">{t('legal.cookies.marketing_help')}</p>
            </li>
          ) : null}
          <li className="space-y-1">
            <label className="inline-flex min-h-6 cursor-pointer items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={functional}
                onChange={(e) => setDraft({ analytics, functional: e.target.checked, marketing })}
                className="size-5 rounded border-input accent-[var(--v2-brand)]"
              />
              {t('legal.cookies.functional')}
            </label>
            <p className="pl-7 text-xs text-muted-foreground">{t('legal.cookies.functional_help')}</p>
          </li>
        </ul>

        <div className="space-y-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="px-0"
            onClick={() => setShowInventory((v) => !v)}
            aria-expanded={showInventory}
          >
            {t('legal.cookies.inventory_title')}
          </Button>
          {showInventory ? (
            <div className="space-y-2 rounded-md border border-border p-3 text-xs text-muted-foreground">
              <p>{t('legal.cookies.inventory_intro')}</p>
              <ul className="list-none space-y-3 p-0">
                {inventoryRows.map((row) => (
                  <li key={row}>
                    <p className="font-medium text-foreground">
                      {t(`legal.cookies.inventory.${row}_name`)}
                    </p>
                    <p>{t(`legal.cookies.inventory.${row}_purpose`)}</p>
                    <p>{t(`legal.cookies.inventory.${row}_duration`)}</p>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>

        <p className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
          <Link
            to="/legal/$legalSlug"
            params={{ legalSlug: 'privacy' }}
            className="text-[var(--v2-brand)] underline underline-offset-4"
          >
            {t('legal.cookies.privacy_link')}
          </Link>
          <Link
            to="/legal/$legalSlug"
            params={{ legalSlug: 'cookies' }}
            className="text-[var(--v2-brand)] underline underline-offset-4"
          >
            {t('legal.cookies.cookies_policy_link')}
          </Link>
        </p>

        <DialogFooter className="flex-wrap gap-2 sm:flex-wrap">
          <Button
            type="button"
            variant="ghost"
            disabled={isSaving}
            className="min-w-0 flex-1 basis-full whitespace-normal text-foreground hover:bg-muted dark:hover:bg-muted/80 sm:flex-none sm:basis-auto"
            onClick={() => persist({ analytics: false, functional: false, marketing: false })}
          >
            {t('legal.cookies.reject_optional')}
          </Button>
          <Button
            type="button"
            variant="outline"
            disabled={isSaving}
            className="min-w-0 flex-1 basis-full whitespace-normal border-border bg-background text-foreground dark:border-border dark:bg-card dark:text-foreground sm:flex-none sm:basis-auto"
            onClick={() => persist({ analytics, functional, marketing })}
          >
            {t('legal.cookies.accept_selected')}
          </Button>
          <Button
            type="button"
            variant="default"
            disabled={isSaving}
            className="min-w-0 flex-1 basis-full whitespace-normal sm:flex-none sm:basis-auto"
            onClick={() => persist({ analytics: true, functional: true, marketing: true })}
          >
            {t('legal.cookies.accept_all')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
